import React, { useState, useEffect } from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td, Badge, HStack, Select, Input, Button, Text } from '@chakra-ui/react';
import { buildAPI } from '../services/api';

export default function BuildHistory({ builds = [] }) {
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState('1h');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  const fetchHistory = () => {
    buildAPI.getHistory().then(({ data }) => setHistory(data)).catch(() => setHistory([]));
  };

  useEffect(() => {
    fetchHistory();
    const interval = setInterval(fetchHistory, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (builds && builds.length > 0) {
      setHistory(builds);
    }
  }, [builds]);

  const displayData = builds && builds.length > 0 ? builds : history;
  if (!Array.isArray(displayData)) return null;

  const statusColor = { success: 'green', failed: 'red', failure: 'red', running: 'blue' };

  const filterHistory = () => {
    const now = new Date();
    let filtered = [...displayData];

    if (filter === '1h') {
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
      filtered = displayData.filter(b => new Date(b.timestamp) >= oneHourAgo);
    } else if (filter === '24h') {
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      filtered = displayData.filter(b => new Date(b.timestamp) >= yesterday);
    } else if (filter === '7d') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = displayData.filter(b => new Date(b.timestamp) >= weekAgo);
    } else if (filter === 'custom' && customStart && customEnd) {
      const start = new Date(customStart);
      const end = new Date(customEnd);
      filtered = displayData.filter(b => {
        const date = new Date(b.timestamp);
        return date >= start && date <= end;
      });
    }

    return filtered;
  };

  const filteredHistory = filterHistory();
  const totalPages = Math.ceil(filteredHistory.length / 5);
  const startIndex = currentPage * 5;
  const displayHistory = filteredHistory.slice(startIndex, startIndex + 5);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(0);
  };

  const handlePrevious = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1);
  };

  return (
    <Box>
      <HStack mb={4} spacing={3} flexWrap="wrap">
        <Select value={filter} onChange={(e) => handleFilterChange(e.target.value)} size="sm" w="150px" color="white" bg="rgba(30, 41, 59, 0.5)" border="1px solid rgba(255,255,255,0.1)">
          <option value="1h" style={{background: '#1e293b', color: 'white'}}>Last 1 hour</option>
          <option value="24h" style={{background: '#1e293b', color: 'white'}}>Last 24 hours</option>
          <option value="7d" style={{background: '#1e293b', color: 'white'}}>Last 7 days</option>
          <option value="custom" style={{background: '#1e293b', color: 'white'}}>Custom range</option>
        </Select>
        
        {filter === 'custom' && (
          <>
            <Input 
              type="datetime-local" 
              size="sm" 
              w="180px" 
              value={customStart}
              onChange={(e) => { setCustomStart(e.target.value); setCurrentPage(0); }}
              placeholder="Start date"
              color="white"
              bg="rgba(30, 41, 59, 0.5)"
              border="1px solid rgba(255,255,255,0.1)"
              sx={{
                '&::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)'
                }
              }}
            />
            <Input 
              type="datetime-local" 
              size="sm" 
              w="180px" 
              value={customEnd}
              onChange={(e) => { setCustomEnd(e.target.value); setCurrentPage(0); }}
              placeholder="End date"
              color="white"
              bg="rgba(30, 41, 59, 0.5)"
              border="1px solid rgba(255,255,255,0.1)"
              sx={{
                '&::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)'
                }
              }}
            />
            <Button 
              size="sm" 
              colorScheme="gray" 
              onClick={() => { setCustomStart(''); setCustomEnd(''); setCurrentPage(0); }}
            >
              Reset
            </Button>
          </>
        )}
        
        <Text fontSize="sm" color="white" ml="auto">
          Showing {startIndex + 1}-{Math.min(startIndex + 5, filteredHistory.length)} of {filteredHistory.length} builds
        </Text>
      </HStack>
      
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th color="white">Build ID</Th>
            <Th color="white">Repository</Th>
            <Th color="white">Provider</Th>
            <Th color="white">Pipeline Type</Th>
            <Th color="white">Status</Th>
            <Th color="white">Timestamp (IST)</Th>
          </Tr>
        </Thead>
        <Tbody>
          {displayHistory.length === 0 ? (
            <Tr>
              <Td colSpan={6} textAlign="center" color="white" py={4}>
                No builds found for selected filter
              </Td>
            </Tr>
          ) : (
            displayHistory.map((build) => (
              <Tr key={build.id}>
                <Td fontFamily="monospace" fontSize="xs" color="white">{build.id.substring(0, 8)}...</Td>
                <Td fontSize="sm" color="white">{build.repo}</Td>
                <Td><Badge colorScheme="purple" fontSize="xs">{build.provider}</Badge></Td>
                <Td><Badge colorScheme="cyan" fontSize="xs">{build.pipeline_type}</Badge></Td>
                <Td><Badge colorScheme={statusColor[build.status]}>{build.status}</Badge></Td>
                <Td fontSize="sm" color="white">{new Date(build.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
      
      {filteredHistory.length > 5 && (
        <HStack justify="center" mt={4} spacing={4}>
          <Button 
            size="sm" 
            onClick={handlePrevious} 
            isDisabled={currentPage === 0}
            leftIcon={<Text>←</Text>}
          >
            Previous
          </Button>
          <Text fontSize="sm" color="white">
            Page {currentPage + 1} of {totalPages}
          </Text>
          <Button 
            size="sm" 
            onClick={handleNext} 
            isDisabled={currentPage >= totalPages - 1}
            rightIcon={<Text>→</Text>}
          >
            Next
          </Button>
        </HStack>
      )}
    </Box>
  );
}
